import React from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';

import { PrimaryButton } from '../Button/ActionButtons';
import { UploadState } from '../../context/LongSessionUploadContext';
import { LongSessionFailureCause, LongSessionStage, WorkoutPublishStatus } from '../../utils/longSession';
import {
  Actions,
  ErrorText,
  HelperText,
  StatusCard,
  StatusRow,
  StyledLinearProgress,
} from './styles';
import texts from './texts.json';

/**
 * Where a video sent through the long-session pipeline stands, as one card: stopped, failed, cut off
 * by a closed tab, or on its way with its stage and this tab's progress.
 *
 * It reads the document's `publishStatus` and `videoProcessing` and this tab's transfer entry, for a
 * long session's workout and a product's welcome video alike. Each panel brings the copy that names
 * what it prepares, and decides when there is anything to show.
 */
const SessionVideoUploadStatus = ({
  publishStatus,
  videoProcessing,
  upload,
  canResume,
  copy,
  notice,
  onCancel,
}) => {
  const hasLiveUpload = upload?.state === UploadState.UPLOADING;

  if (publishStatus === WorkoutPublishStatus.FAILED) {
    const { stage: failedStage, cause } = videoProcessing?.error || {};
    // A coach who stopped the upload themselves does not need it reported as a fault.
    if (cause === LongSessionFailureCause.CANCELLED) {
      return (
        <StatusCard>
          <StatusRow>{texts.uploadCancelled}</StatusRow>
          <HelperText>{copy.cancelledExplanation}</HelperText>
        </StatusCard>
      );
    }
    return (
      <StatusCard>
        <StatusRow>{copy.failed}</StatusRow>
        {!!failedStage && (
          <ErrorText>{format(texts.failedAtStage, { stage: texts.stage[failedStage] || failedStage })}</ErrorText>
        )}
        <HelperText>{copy.failedExplanation}</HelperText>
      </StatusCard>
    );
  }

  if (upload?.state === UploadState.FAILED) {
    return (
      <StatusCard>
        <StatusRow>{texts.uploadFailed}</StatusRow>
        <HelperText>{texts.uploadFailedExplanation}</HelperText>
      </StatusCard>
    );
  }

  if (canResume) {
    return (
      <StatusCard>
        <StatusRow>{texts.sendAgain}</StatusRow>
        <HelperText>{texts.sendAgainExplanation}</HelperText>
      </StatusCard>
    );
  }

  // The document still reads UPLOADING until the server picks the file up; say what happened.
  const stage = upload?.state === UploadState.COMPLETED && videoProcessing?.stage === LongSessionStage.UPLOADING
    ? LongSessionStage.UPLOADED
    : videoProcessing?.stage;

  return (
    <StatusCard>
      {!!notice && <HelperText>{notice}</HelperText>}
      <StatusRow>
        <span>{texts.stage[stage] || texts.stage[LongSessionStage.UPLOADING]}</span>
        {hasLiveUpload && <span>{`${upload.progress}%`}</span>}
      </StatusRow>
      <StyledLinearProgress
        variant={hasLiveUpload ? 'determinate' : 'indeterminate'}
        value={upload?.progress}
      />
      {hasLiveUpload && <HelperText>{copy.reassurance}</HelperText>}
      {hasLiveUpload && !!onCancel && (
        <Actions>
          <PrimaryButton variant="info" size="small" onClick={onCancel}>
            {texts.cancelUpload}
          </PrimaryButton>
        </Actions>
      )}
    </StatusCard>
  );
};

SessionVideoUploadStatus.propTypes = {
  publishStatus: PropTypes.string,
  /** The document's `videoProcessing`: its stage and, once it failed, the error. */
  videoProcessing: PropTypes.shape({
    stage: PropTypes.string,
    error: PropTypes.shape({
      stage: PropTypes.string,
      cause: PropTypes.string,
    }),
  }),
  /** This tab's transfer entry for the document, from the upload provider. */
  upload: PropTypes.shape({
    state: PropTypes.string,
    progress: PropTypes.number,
  }),
  canResume: PropTypes.bool,
  /** What the panel prepares, in its own words. */
  copy: PropTypes.shape({
    failed: PropTypes.string.isRequired,
    failedExplanation: PropTypes.string.isRequired,
    cancelledExplanation: PropTypes.string.isRequired,
    reassurance: PropTypes.string.isRequired,
  }).isRequired,
  /** A line above the stage, e.g. that a live video stays up while its replacement processes. */
  notice: PropTypes.string,
  /** Offered as a button while the bytes are moving. */
  onCancel: PropTypes.func,
};

SessionVideoUploadStatus.defaultProps = {
  publishStatus: undefined,
  videoProcessing: undefined,
  upload: undefined,
  canResume: false,
  notice: '',
  onCancel: undefined,
};

export default SessionVideoUploadStatus;
